import React, { useRef, useEffect, useCallback } from 'react';
import Editor, { OnMount, OnChange } from '@monaco-editor/react';
import type * as Monaco from 'monaco-editor';
import { useScriptEditorStore } from '../../store/scriptEditorStore';
import { useMonacoSetup } from '../../hooks/useMonaco';

interface MonacoWrapperProps {
  fileId: string;
  className?: string;
}

export const MonacoWrapper: React.FC<MonacoWrapperProps> = ({ fileId, className }) => {
  const editorRef = useRef<Monaco.editor.IStandaloneCodeEditor | null>(null);

  const {
    files,
    settings,
    updateFileContent,
    updateCursorPosition,
    saveFile,
    autoCompile,
    compile,
  } = useScriptEditorStore();

  // Setup Monaco configuration (themes, type definitions)
  // Note: Monaco workers are already configured in index.tsx via monaco-setup.ts
  useMonacoSetup();

  const file = files[fileId];

  const handleEditorDidMount: OnMount = useCallback((editor, monacoInstance) => {
    editorRef.current = editor;

    // Restore cursor position
    if (file) {
      editor.setPosition({
        lineNumber: file.cursorPosition.line,
        column: file.cursorPosition.column,
      });
      editor.focus();
    }

    // Add keyboard shortcuts
    editor.addCommand(monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyCode.KeyS, () => {
      saveFile(fileId);
    });

    // Go to definition
    editor.addCommand(monacoInstance.KeyCode.F12, () => {
      editor.getAction('editor.action.revealDefinition')?.run();
    });

    // Find references
    editor.addCommand(monacoInstance.KeyMod.Shift | monacoInstance.KeyCode.F12, () => {
      editor.getAction('editor.action.goToReferences')?.run();
    });

    // Rename symbol
    editor.addCommand(monacoInstance.KeyCode.F2, () => {
      editor.getAction('editor.action.rename')?.run();
    });

    // Quick fix
    editor.addCommand(monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyCode.Period, () => {
      editor.getAction('editor.action.quickFix')?.run();
    });

    // Format document
    editor.addCommand(monacoInstance.KeyMod.Shift | monacoInstance.KeyMod.Alt | monacoInstance.KeyCode.KeyF, () => {
      editor.getAction('editor.action.formatDocument')?.run();
    });
  }, [file, fileId, saveFile]);

  const handleChange: OnChange = useCallback((value) => {
    if (value !== undefined && value !== file?.content) {
      updateFileContent(fileId, value);
    }
  }, [fileId, file?.content, updateFileContent]);

  const handleCursorChange = useCallback(() => {
    const editor = editorRef.current;
    if (editor) {
      const position = editor.getPosition();
      if (position) {
        updateCursorPosition(fileId, position.lineNumber, position.column);
      }
    }
  }, [fileId, updateCursorPosition]);

  // Handle cursor position changes
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const disposable = editor.onDidChangeCursorPosition(handleCursorChange);
    return () => disposable.dispose();
  }, [handleCursorChange]);

  // Track content changes for autocompile without triggering effect on every keystroke
  const shouldCompileRef = useRef(false);
  const prevContentRef = useRef(file?.content);

  // Mark for compilation when content actually changes
  useEffect(() => {
    if (file?.content !== prevContentRef.current) {
      prevContentRef.current = file?.content;
      if (file?.isDirty) {
        shouldCompileRef.current = true;
      }
    }
  }, [file?.content, file?.isDirty]);

  // Handle auto-compile on change (debounced) - NO file?.content in deps!
  useEffect(() => {
    if (!autoCompile || !file?.isDirty) return;

    const timeout = setTimeout(() => {
      if (shouldCompileRef.current) {
        shouldCompileRef.current = false;
        compile();
      }
    }, 1000);

    return () => clearTimeout(timeout);
  }, [autoCompile, file?.isDirty, compile]);

  if (!file) {
    return (
      <div className={`flex items-center justify-center h-full bg-zinc-900 text-zinc-500 ${className}`}>
        No file selected
      </div>
    );
  }

  return (
    <div className={`h-full w-full ${className}`}>
      <Editor
        height="100%"
        language={file.language}
        value={file.content}
        theme="pixijs-dark"
        onChange={handleChange}
        onMount={handleEditorDidMount}
        options={{
          fontSize: settings.fontSize,
          fontFamily: settings.fontFamily,
          tabSize: settings.tabSize,
          insertSpaces: settings.insertSpaces,
          wordWrap: settings.wordWrap,
          minimap: { enabled: settings.minimap },
          lineNumbers: settings.lineNumbers,
          automaticLayout: true,
          scrollBeyondLastLine: false,
          renderWhitespace: 'selection',
          bracketPairColorization: { enabled: true },
          guides: {
            bracketPairs: true,
            indentation: true,
          },
          suggest: {
            showKeywords: true,
            showSnippets: true,
            showClasses: true,
            showFunctions: true,
            showVariables: true,
            showConstants: true,
            showInterfaces: true,
            showModules: true,
            showProperties: true,
            showEvents: true,
            showMethods: true,
          },
          quickSuggestions: {
            other: true,
            comments: true,
            strings: true,
          },
          parameterHints: { enabled: true },
          folding: true,
          foldingStrategy: 'indentation',
          showFoldingControls: 'mouseover',
          smoothScrolling: true,
          cursorBlinking: 'smooth',
          cursorSmoothCaretAnimation: 'on',
          formatOnPaste: settings.formatOnPaste,
          formatOnType: true,
        }}
        loading={
          <div className="flex items-center justify-center h-full bg-zinc-900 text-zinc-400">
            Loading editor...
          </div>
        }
      />
    </div>
  );
};
